import { useEffect, type ReactNode } from "react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  icon?: ReactNode;
  iconBgColor?: string;
  iconColor?: string;
}

export default function Modal({
  isOpen,
  onClose,
  children,
  title,
  icon,
  iconBgColor = "bg-yellow-100",
  iconColor = "text-yellow-600",
}: ModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md mx-4 transform transition-all duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {icon && (
            <div
              className={`flex items-center justify-center w-12 h-12 mx-auto mb-4 ${iconBgColor} rounded-full`}
            >
              <div className={`w-6 h-6 ${iconColor}`}>{icon}</div>
            </div>
          )}

          {title && (
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {title}
            </h3>
          )}

          {children}
        </div>
      </div>
    </div>
  );
}
